import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { Section } from '../ui/Section';
import { Card } from '../ui/Card';
import { GeometricPattern } from '../../utils/patterns';
import { typography } from '../../utils/theme';
import { LinkedinIcon, GithubIcon, TwitterIcon, CheckCircleIcon, SendIcon, MailIcon, MessageCircleIcon } from 'lucide-react';
// Custom SVG Icons for contact
const CustomMailIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className} data-id="element-109">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" data-id="element-110" />
    <path d="M3 7L12 13L21 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-111" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" data-id="element-112" />
  </svg>;
const CustomSendIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" className={className} data-id="element-113">
    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-114" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" data-id="element-115" />
    <circle cx="11" cy="13" r="1" fill="currentColor" data-id="element-116" />
  </svg>;
export function ContactApp() {
  const {
    getThemeClasses,
    t
  } = useTheme();
  const themeClasses = getThemeClasses();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };
  const socialLinks = [{
    icon: LinkedinIcon,
    label: 'LinkedIn',
    url: '#',
    color: 'from-blue-500 to-blue-600'
  }, {
    icon: GithubIcon,
    label: 'GitHub',
    url: '#',
    color: 'from-gray-700 to-gray-800'
  }, {
    icon: TwitterIcon,
    label: 'Twitter',
    url: '#',
    color: 'from-sky-500 to-sky-600'
  }];
  return <div className={`p-4 md:p-6 lg:p-8 ${themeClasses.text} max-w-4xl mx-auto`} data-id="element-117">
      {/* Header with pinboard style */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-8" data-id="element-118">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3" data-id="element-119">
          {t.contact?.title || 'Contact'}
        </h1>
        <p className={`text-base md:text-lg ${themeClasses.text} opacity-70 max-w-3xl`} data-id="element-120">
          {t.contact?.description || 'Neem contact met me op om te bespreken hoe ik kan helpen.'}
        </p>
      </motion.div>
      {/* Availability badge */}
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4 mb-6 flex items-center gap-3 relative`} style={{
      boxShadow: '0 8px 20px -8px rgba(0, 0, 0, 0.2)',
      transform: 'rotate(-0.5deg)'
    }} data-id="element-121">
        <div className="relative" data-id="element-122">
          <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-123" />
          <motion.div className="absolute inset-0 w-3 h-3 rounded-full bg-green-500" animate={{
          scale: [1, 1.5, 1],
          opacity: [0.5, 0, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-124" />
        </div>
        <div data-id="element-125">
          <p className="text-sm font-semibold" data-id="element-126">
            Beschikbaar voor nieuwe projecten
          </p>
          <p className="text-xs opacity-60" data-id="element-127">Meestal binnen 24 uur reactie</p>
        </div>
        {/* Pin effect */}
        <div className="absolute -top-2 right-4 w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-128" />
      </motion.div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-129">
        {/* Contact form - styled card */}
        <div className="lg:col-span-2 relative" data-id="element-130">
          <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl`} style={{
          boxShadow: '0 15px 35px -10px rgba(0, 0, 0, 0.2)',
          transform: 'rotate(0.5deg)'
        }} data-id="element-131">
            {/* Gradient header */}
            <div className="relative p-6 bg-gradient-to-br from-purple-500 to-pink-500 text-white" data-id="element-132">
              <div className="absolute inset-0 opacity-20" data-id="element-133">
                <GeometricPattern pattern="circles" size="large" data-id="element-134" />
              </div>
              <div className="relative z-10 flex items-center gap-3" data-id="element-135">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-136">
                  <MessageCircleIcon className="w-6 h-6 text-white" data-id="element-137" />
                </div>
                <div data-id="element-138">
                  <h2 className="text-xl font-bold" data-id="element-139">Stuur een Bericht</h2>
                  <p className="text-white/80 text-sm" data-id="element-140">
                    Ik reageer meestal binnen 24 uur
                  </p>
                </div>
              </div>
            </div>
            {/* Form content */}
            <div className="p-6" data-id="element-141">
              {!isSubmitted ? <form onSubmit={handleSubmit} className="space-y-4" data-id="element-142">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-143">
                    <div className="relative" data-id="element-144">
                      <label className="block text-sm font-medium mb-2" data-id="element-145">
                        {t.contact?.form?.name || 'Naam'}
                      </label>
                      <input type="text" value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)} className={`w-full px-4 py-3 rounded-lg ${themeClasses.card} border ${themeClasses.border} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all`} required data-id="element-146" />
                      {focusedField === 'name' && <motion.div layoutId="focus-indicator" className="absolute -left-1 top-9 w-1 h-10 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full" transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 30
                  }} data-id="element-147" />}
                    </div>
                    <div className="relative" data-id="element-148">
                      <label className="block text-sm font-medium mb-2" data-id="element-149">
                        {t.contact?.form?.email || 'Email'}
                      </label>
                      <input type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} className={`w-full px-4 py-3 rounded-lg ${themeClasses.card} border ${themeClasses.border} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all`} required data-id="element-150" />
                      {focusedField === 'email' && <motion.div layoutId="focus-indicator" className="absolute -left-1 top-9 w-1 h-10 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full" transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 30
                  }} data-id="element-151" />}
                    </div>
                  </div>
                  <div data-id="element-152">
                    <label className="block text-sm font-medium mb-2" data-id="element-153">
                      {t.contact?.form?.subject || 'Onderwerp'}
                    </label>
                    <input type="text" value={formData.subject} onChange={e => setFormData({
                  ...formData,
                  subject: e.target.value
                })} className={`w-full px-4 py-3 rounded-lg ${themeClasses.card} border ${themeClasses.border} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all`} required data-id="element-154" />
                  </div>
                  <div data-id="element-155">
                    <label className="block text-sm font-medium mb-2" data-id="element-156">
                      {t.contact?.form?.message || 'Bericht'}
                    </label>
                    <textarea value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} rows={6} className={`w-full px-4 py-3 rounded-lg ${themeClasses.card} border ${themeClasses.border} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none transition-all`} required data-id="element-157" />
                  </div>
                  <button type="submit" className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:scale-[1.02] transition-all shadow-lg flex items-center justify-center gap-2" data-id="element-158">
                    <SendIcon className="w-5 h-5" data-id="element-159" />
                    {t.contact?.form?.submit || 'Verstuur bericht'}
                  </button>
                </form> : <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-12" data-id="element-160">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-4 relative overflow-hidden" style={{
                boxShadow: '0 12px 24px -8px rgba(16, 185, 129, 0.4)'
              }} data-id="element-161">
                    <div className="absolute inset-0 opacity-20" data-id="element-162">
                      <GeometricPattern pattern="circles" size="small" data-id="element-163" />
                    </div>
                    <CheckCircleIcon className="w-8 h-8 text-white relative z-10" data-id="element-164" />
                  </div>
                  <h3 className={`${typography.h3} mb-2`} data-id="element-165">
                    {t.contact?.success?.title || 'Bericht verstuurd!'}
                  </h3>
                  <p className="opacity-80" data-id="element-166">
                    {t.contact?.success?.message || 'Bedankt voor je bericht. Ik neem zo snel mogelijk contact met je op.'}
                  </p>
                </motion.div>}
            </div>
          </div>
          {/* Pin effect */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-167" />
        </div>
        {/* Social links - styled cards */}
        <div className="space-y-4" data-id="element-168">
          <div className="relative" data-id="element-169">
            <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl p-6`} style={{
            boxShadow: '0 10px 25px -8px rgba(0, 0, 0, 0.2)',
            transform: 'rotate(-1deg)'
          }} data-id="element-170">
              <div className="flex items-center gap-3 mb-4" data-id="element-171">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center relative overflow-hidden" style={{
                boxShadow: '0 8px 16px -4px rgba(59, 130, 246, 0.4)'
              }} data-id="element-172">
                  <GeometricPattern pattern="circles" size="small" className="opacity-30" data-id="element-173" />
                  <MailIcon className="w-5 h-5 text-white relative z-10" data-id="element-174" />
                </div>
                <div data-id="element-175">
                  <p className="text-sm opacity-60" data-id="element-176">Direct contact</p>
                  <p className="font-semibold" data-id="element-177">hello@example.com</p>
                </div>
              </div>
            </div>
            <div className="absolute -top-2 right-4 w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-178" />
          </div>
          <div className="relative" data-id="element-179">
            <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl p-6`} style={{
            boxShadow: '0 10px 25px -8px rgba(0, 0, 0, 0.2)',
            transform: 'rotate(1.5deg)'
          }} data-id="element-180">
              <h3 className={`${typography.h4} mb-4`} data-id="element-181">Volg mij</h3>
              <div className="space-y-3" data-id="element-182">
                {socialLinks.map(social => {
                const Icon = social.icon;
                return <a key={social.label} href={social.url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 p-3 rounded-lg ${themeClasses.card} border ${themeClasses.border} hover:scale-[1.02] transition-all group`} data-id="element-183">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${social.color} flex items-center justify-center group-hover:scale-110 transition-transform`} style={{
                    boxShadow: '0 4px 12px -4px rgba(0, 0, 0, 0.3)'
                  }} data-id="element-184">
                        <Icon className="w-4 h-4 text-white" data-id="element-185" />
                      </div>
                      <span className="font-medium" data-id="element-186">{social.label}</span>
                    </a>;
              })}
              </div>
            </div>
            <div className="absolute -top-2 left-4 w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-187" />
          </div>
        </div>
      </div>
    </div>;
}